import type { ModalContentItem } from './types';

type ModalClonedContentPrepareCallback = (cloned_content: Node) => void;

/**
 * // Class Obj: モーダル内へ汎用コンテンツを描画
 * // Note:
 * // - data-modal-content-display を描画先として扱います。
 * // - 表示元 content は clone して描画するため、元DOMは壊しません。
 * // - template 要素にも対応し、任意HTMLの差し替えに使えるようにしています。
 */
export class ModalContentRenderer
{
  private static readonly SELECTOR_CONTENT_DISPLAY = '[data-modal-content-display]';

  /**
   * // Method: 指定 item の content を対象モーダル内へ描画する
   */
  public render(
    content_item: ModalContentItem,
    modal: HTMLElement | null,
    prepare_cloned_content: ModalClonedContentPrepareCallback | null = null,
  ): HTMLElement | null
  {
    const content_display: HTMLElement | null = this.getContentDisplay(modal);

    if (!content_display)
    {
      return null;
    }

    const cloned_content: Node = this.cloneContent(content_item.content);

    prepare_cloned_content?.(cloned_content);
    content_display.replaceChildren(cloned_content);

    return content_display;
  }


  /**
   * // Method: 描画先に複製した content を削除する
   */
  public clear(modal: HTMLElement | null): void
  {
    const content_display: HTMLElement | null = this.getContentDisplay(modal);

    if (!content_display)
    {
      return;
    }

    content_display.replaceChildren();
  }

  /**
   * // Method: 汎用コンテンツの描画先を取得する
   */
  public getContentDisplay(modal: HTMLElement | null): HTMLElement | null
  {
    if (!modal)
    {
      return null;
    }

    return modal.querySelector<HTMLElement>(ModalContentRenderer.SELECTOR_CONTENT_DISPLAY);
  }

  /**
   * // Method: 表示元 content を描画用に複製する
   * // Note:
   * // - hidden / aria-hidden が付いている元要素でも、モーダル内では表示できるように属性を外します。
   */
  private cloneContent(content: HTMLElement): Node
  {
    if (content instanceof HTMLTemplateElement)
    {
      return content.content.cloneNode(true);
    }

    const cloned_content: HTMLElement = content.cloneNode(true) as HTMLElement;
    cloned_content.removeAttribute('hidden');
    cloned_content.removeAttribute('aria-hidden');

    return cloned_content;
  }
}
